import * as React from "react"

import { cn } from "@/lib/utils"

function Table({ className, ...props }: React.ComponentProps<"table">) {
  const scrollRef = React.useRef<HTMLDivElement>(null)

  return (
    <div className="w-full min-w-0">
      <div
        ref={scrollRef}
        data-slot="table-container"
        className="native-scrollbar-hidden relative w-full overflow-x-auto"
      >
        <table
          data-slot="table"
          className={cn(" table-auto caption-bottom text-sm w-full min-w-0", className)}
          {...props}
        />
      </div>
      <TableScrollBar scrollRef={scrollRef} />
    </div>
  )
}

function TableScrollBar({
  scrollRef,
}: {
  scrollRef: React.RefObject<HTMLDivElement | null>
}) {
  const trackRef = React.useRef<HTMLDivElement>(null)
  const dragRef = React.useRef<{ pointerId: number; startX: number; startScroll: number } | null>(null)
  const [metrics, setMetrics] = React.useState({ visible: false, thumbWidth: 0, thumbLeft: 0 })

  const measure = React.useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    if (maxScroll <= 1) {
      setMetrics((prev) => (prev.visible ? { visible: false, thumbWidth: 0, thumbLeft: 0 } : prev))
      return
    }
    const thumbWidth = Math.max((el.clientWidth / el.scrollWidth) * 100, 12)
    const thumbLeft = (el.scrollLeft / maxScroll) * (100 - thumbWidth)
    setMetrics((prev) => {
      if (
        prev.visible &&
        Math.abs(prev.thumbWidth - thumbWidth) < 0.2 &&
        Math.abs(prev.thumbLeft - thumbLeft) < 0.2
      ) {
        return prev
      }
      return { visible: true, thumbWidth, thumbLeft }
    })
  }, [scrollRef])

  React.useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    measure()
    el.addEventListener("scroll", measure, { passive: true })
    const observer = new ResizeObserver(() => measure())
    observer.observe(el)
    const table = el.querySelector("table")
    if (table) observer.observe(table)
    window.addEventListener("resize", measure)
    return () => {
      el.removeEventListener("scroll", measure)
      observer.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [measure, scrollRef])

  if (!metrics.visible) return null

  const scrollFromClientX = (clientX: number, startX: number, startScroll: number) => {
    const el = scrollRef.current
    const track = trackRef.current
    if (!el || !track) return
    const maxScroll = el.scrollWidth - el.clientWidth
    const thumbPx = (metrics.thumbWidth / 100) * track.clientWidth
    const travel = Math.max(track.clientWidth - thumbPx, 1)
    el.scrollLeft = startScroll + ((clientX - startX) / travel) * maxScroll
  }

  return (
    <div
      ref={trackRef}
      className="mt-1.5 h-2.5 w-full cursor-pointer rounded-full bg-[#E5E7EB]"
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget) return
        const el = scrollRef.current
        const track = trackRef.current
        if (!el || !track) return
        const rect = track.getBoundingClientRect()
        const thumbPx = (metrics.thumbWidth / 100) * rect.width
        const maxScroll = el.scrollWidth - el.clientWidth
        const travel = Math.max(rect.width - thumbPx, 1)
        const clickPx = event.clientX - rect.left - thumbPx / 2
        el.scrollLeft = (Math.min(Math.max(clickPx, 0), travel) / travel) * maxScroll
      }}
    >
      <div
        className="h-full touch-none rounded-full bg-[#9CA3AF]"
        style={{ width: `${metrics.thumbWidth}%`, marginLeft: `${metrics.thumbLeft}%` }}
        onPointerDown={(event) => {
          event.stopPropagation()
          const el = scrollRef.current
          if (!el) return
          dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startScroll: el.scrollLeft,
          }
          event.currentTarget.setPointerCapture(event.pointerId)
        }}
        onPointerMove={(event) => {
          const drag = dragRef.current
          if (!drag || drag.pointerId !== event.pointerId) return
          scrollFromClientX(event.clientX, drag.startX, drag.startScroll)
        }}
        onPointerUp={(event) => {
          if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null
        }}
        onPointerCancel={(event) => {
          if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null
        }}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b", className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-muted/50 border-t font-medium [&>tr]:last:border-b-0",
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors",
        className
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
